import { Send, Paperclip, XCircle, Loader2, Printer } from 'lucide-react';

interface Props {
  inputMessage: string;
  selectedFiles: File[];
  isSending: boolean;
  isConnecting: boolean;
  isLoadingAssistants: boolean;
  activeAssistant: any;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onInputChange: (val: string) => void;
  onFileClick: () => void;
  onSendMessage: () => void;
  onEndConversation: () => void;
  onKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onFileRemove: (index: number) => void;
  onPrintConversation?: () => void;
}

export function ChatInputPanel({
  inputMessage,
  selectedFiles,
  isSending,
  isConnecting,
  isLoadingAssistants,
  activeAssistant,
  inputRef,
  onInputChange,
  onFileClick,
  onSendMessage,
  onEndConversation,
  onKeyPress,
  onFileRemove,
  onPrintConversation
}: Props) {
  return (
    <div className="bg-white shadow-sm border-t border-gray-200 p-4">
      <div className="flex flex-col space-y-4">
        {selectedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center bg-white px-3 py-1 rounded-full border shadow-sm">
                <span className="text-sm text-gray-600 truncate max-w-[200px]">{file.name}</span>
                <button
                  onClick={() => onFileRemove(index)}
                  className="ml-2 text-gray-400 hover:text-red-500"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <textarea
          ref={inputRef}
          value={inputMessage}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyPress={onKeyPress}
          placeholder="Escribe un mensaje..."
          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none shadow-sm"
          rows={3}
          disabled={!activeAssistant || isConnecting || isLoadingAssistants || isSending}
        />

        <div className="flex justify-center space-x-4">
          <button
            onClick={onFileClick}
            disabled={!activeAssistant || isConnecting || isLoadingAssistants || isSending}
            className="p-3 bg-gray-500 text-white rounded-full hover:bg-gray-600 disabled:opacity-50 shadow-lg group"
            title="Adjuntar archivos"
          >
            <Paperclip className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>

          <button
            onClick={onSendMessage}
            disabled={!activeAssistant || (!inputMessage.trim() && selectedFiles.length === 0) || isConnecting || isLoadingAssistants || isSending}
            className="p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 shadow-lg group"
            title="Enviar mensaje"
          >
            {isSending ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <Send className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            )}
          </button>

          <button
            onClick={onEndConversation}
            disabled={!activeAssistant || isConnecting || isLoadingAssistants || isSending}
            className="p-3 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50 shadow-lg group"
            title="Finalizar conversación"
          >
            <XCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>
        </div>

        {/* Print button below the main action buttons */}
        {activeAssistant && onPrintConversation && (
          <div className="flex justify-center mt-2">
            <button
              onClick={onPrintConversation}
              disabled={isConnecting || isLoadingAssistants || isSending}
              className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
            >
              <Printer className="w-4 h-4 mr-2" />
              <span>Imprimir conversación</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}